import React, { useState, useEffect } from 'react';
import { Container, Grid, Typography, CardHeader, Button, TextField, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import MaterialTable from 'material-table';
import { Icons } from '../utils/Icon';
import { CustomStyles } from '../utils/Styled';
import axios from 'axios';
import Swal from 'sweetalert2';
import { Countries } from '../utils/Lookup';

export function EmailTemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    body: '',
    category: '',
    country_id: '',
  });
  const [errors, setErrors] = useState({
    name: '',
    subject: '',
    body: '',
    category: '',
    country_id: '',
  });
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(10);

  const classes = CustomStyles;

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_WEBSITE_BACKEND_URL}api/v1/email-templates`);
      setTemplates(response.data.data.emailTemplates || []);
      setLoading(false);
    } catch (err) {
      Swal.fire({
        title: 'Error!',
        text: 'Failed to fetch email templates',
        icon: 'error',
        confirmButtonColor: '#fe7320'
      });
      setTemplates([]);
      setLoading(false);
    }
  };

  const handleEdit = (template) => {
    setSelectedTemplate(template);
    setFormData({
      name: template.name,
      subject: template.subject,
      body: template.body,
      category: template.category || '',
      country_id: template.country_id || '',
    });
    setOpenDialog(true);
  };

  const handleClose = () => {
    setOpenDialog(false);
    setSelectedTemplate(null);
    setFormData({
      name: '',
      subject: '',
      body: '',
      category: '',
      country_id: '',
    });
    setErrors({
      name: '',
      subject: '',
      body: '',
      category: '',
      country_id: '',
    });
  };

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    if (!formData.name.trim()) {
      newErrors.name = 'Template name is required';
      isValid = false;
    }

    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
      isValid = false;
    }

    if (!formData.body.trim()) {
      newErrors.body = 'Body is required';
      isValid = false;
    }

    if (!formData.category.trim()) {
      newErrors.category = 'Category is required';
      isValid = false;
    }

    if (!formData.country_id) {
      newErrors.country_id = 'Country is required';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setUpdating(true);

      const response = await axios.patch(`${process.env.REACT_APP_WEBSITE_BACKEND_URL}api/v1/email-templates/${selectedTemplate.id}`, formData);

      if (response && response.status === 200) {
        Swal.fire({
          title: 'Success!',
          text: 'Template updated successfully',
          icon: 'success',
          showConfirmButton: false,
          timer: 2000
        });
        handleClose();
        fetchTemplates();
      } else {
        Swal.fire({
          title: 'Error!',
          text: 'Failed to update template: Invalid response',
          icon: 'error',
          confirmButtonColor: '#fe7320'
        });
      }
    } catch (err) {
      Swal.fire({
        title: 'Error!',
        text: err.message || 'Failed to update template',
        icon: 'error',
        confirmButtonColor: '#fe7320'
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: '',
      });
    }
  };

  const columns = [
    {
      title: 'Name',
      field: 'name',
    },
    {
      title: 'Category',
      field: 'category',
      render: (rowData) => rowData.category || '-',
    },
    {
      title: 'Subject',
      field: 'subject',
    },
    {
      title: 'Country',
      field: 'country_id',
      render: (rowData) => {
        const country = Countries.find(c => c.id === rowData.country_id);
        return country ? country.name : '-';
      },
    },
  ];

  const actions = [
    {
      icon: () => <Icons.Edit />,
      tooltip: 'Edit Template',
      onClick: (event, rowData) => handleEdit(rowData),
    }
  ];

  if (loading) {
    return (
      <div>
        <div className={classes.background} />
        <Container maxWidth="xl" sx={{ py: 2 }} style={{ padding: 14 }}>
        </Container>
      </div>
    );
  }

  return (
    <div>
      <div className={classes.background} />
      <Container maxWidth="xl" sx={{ py: 2 }} style={{ padding: 14 }}>
        <MaterialTable
          title="Email Templates"
          icons={Icons}
          columns={columns}
          data={templates}
          page={page}
          totalCount={templates.length}
          style={{ padding: "24px" }}
          options={{
            pageSize: perPage,
            pageSizeOptions: [10, 20, 30, 50],
            search: false,
            filtering: false,
            sorting: false,
            actionsColumnIndex: -1,
            headerStyle: {
              fontWeight: "bold",
              backgroundColor: "#f1f1f1",
              color: "#212122",
            },
            maxBodyHeight: "600px",
            paginationType: "stepped",
            padding: "dense",
            draggable: false,
          }}
          localization={{
            pagination: {
              labelRowsPerPage: "",
              labelDisplayedRows: "{from}-{to} of {count}",
              labelRowsSelect: "rows",
            },
            toolbar: {
              nRowsSelected: "{0} row(s) selected",
            },
            header: {
              actions: "Actions",
            },
            body: {
              emptyDataSourceMessage: "No data to display",
              filterRow: {
                filterTooltip: "Filter",
              },
            },
          }}
          actions={actions}
          onChangePage={(page) => setPage(page)}
          onChangeRowsPerPage={(perPage) => setPerPage(perPage)}
          components={{
            Toolbar: (props) => (
              <>
                <div>
                  <Grid container spacing={2} sx={{ px: 0.1, pb: 3.5 }}>
                    <Grid item xs={12} sm={3}>
                      <CardHeader
                        sx={{ p: 0 }}
                        title={
                          <Typography component="h1" variant="h5" color="primary" fontWeight="bold">
                            Email Templates
                          </Typography>
                        }
                      />
                    </Grid>
                  </Grid>
                </div>
              </>
            ),
          }}
        />

        <Dialog open={openDialog} onClose={handleClose} maxWidth="md" fullWidth>
          <DialogTitle>Edit Email Template</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              name="name"
              label="Template Name"
              type="text"
              autoComplete="off"
              fullWidth
              value={formData.name}
              onChange={handleChange}
              InputProps={{
                readOnly: true,
                sx: {
                  backgroundColor: '#f5f5f5',
                  '&:hover': {
                    backgroundColor: '#f5f5f5',
                  },
                }
              }}
              error={!!errors.name}
              helperText={errors.name}
            />
            <TextField
              margin="dense"
              name="category"
              label="Category"
              type="text"
              fullWidth
              value={formData.category}
              onChange={handleChange}
              InputProps={{
                readOnly: true,
                sx: {
                  backgroundColor: '#f5f5f5',
                  '&:hover': {
                    backgroundColor: '#f5f5f5',
                  },
                }
              }}
              error={!!errors.category}
              helperText={errors.category}
            />
            <TextField
              margin="dense"
              name="subject"
              label="Subject"
              type="text"
              fullWidth
              value={formData.subject}
              onChange={handleChange}
              error={!!errors.subject}
              helperText={errors.subject}
            />
            <TextField
              margin="dense"
              name="body"
              label="Body"
              type="text"
              fullWidth
              multiline
              rows={10}
              value={formData.body}
              onChange={handleChange}
              error={!!errors.body}
              helperText={errors.body}
              style={{ marginBottom: "10px" }}
            />
            <TextField
              margin="dense"
              name="country_id"
              label="Country"
              type="text"
              fullWidth
              value={(() => {
                const country = Countries.find(c => c.id === formData.country_id);
                return country ? country.name : '';
              })()}
              InputProps={{
                readOnly: true,
                sx: {
                  backgroundColor: '#f5f5f5',
                  '&:hover': {
                    backgroundColor: '#f5f5f5',
                  },
                }
              }}
              error={!!errors.country_id}
              helperText={errors.country_id}
            />
          </DialogContent>
          <DialogActions style={{ margin: "12px" }}>
            <Button 
              onClick={handleSubmit} 
              variant="contained" 
              color="primary"
              disabled={updating}
              style={{ marginRight: "6px", color: "white" }}
            >
              {updating ? 'Saving...' : 'Update'}
            </Button>
            <Button 
              onClick={handleClose} 
              variant="contained" 
              color="error"
              disabled={updating}
              style={{ marginRight: "6px", color: "white" }}
            >
              Cancel
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </div>
  );
} 